/**
 * The GLOBAL right drawer — the shell, shared by every app that has one (#543).
 *
 * The Elevate-sidepane pattern at shell level (user steer 2026-07-13: "the
 * drawer is a global thing"). Its tenants are cross-entity lenses bound to
 * whatever record the current page registered via `drawer/context`.
 *
 * ── THE DRAWER IS ABSENT UNTIL IT HAS SOMETHING HONEST TO OFFER (F-30C) ─────
 * This header used to argue the opposite, in these words: *"No record on screen
 * ⇒ the tenants explain themselves instead of vanishing, because a rail whose
 * contents change as you navigate is one nobody learns."* That argument is
 * OVERTURNED by #1466 comment 43562 ruling 3, and the replacement is not a
 * preference — it is what the two hosts measure differently.
 *
 * The old argument assumed a rail that always has tenants worth explaining. In
 * the CONSOLE that is true: `AppShell.tsx` declares a binding on every route
 * change, and four of its panels are `standalone` (navigation, work queues,
 * copilot history, capture) — each answers a question with no record on screen
 * at all. In the PORTAL it is false, and not marginally: `setDrawerContext` is
 * called from exactly ONE place in the whole application
 * (`apps/portal/src/pages/Approvals.tsx`, inside a "Review context and decide"
 * button), so on 23 of 24 customer destinations `useDrawerContext()` is null —
 * and the portal registers NO standalone panel. An always-present rail there is
 * a permanently empty stripe: mystery chrome, not learnable navigation. A
 * disabled or empty affordance is not guidance.
 *
 * So the condition is DERIVED from the panels rather than flagged per host
 * (rule 42 — a host flag would be a second, drifting statement of a fact the
 * panel list already carries):
 *
 *     offers = a record is bound  OR  some permitted panel is `standalone`
 *
 * The console satisfies it on every route by construction; the portal satisfies
 * it exactly when a customer has opened a decision. Neither host names itself.
 *
 * When `offers` is false the component still MOUNTS, as a zero-width wrap — the
 * shape F-30B established for compact-no-context — because `--drawer-w` is
 * published from `wrap.offsetWidth` and read by the kit's own PeekShell and one console composer.
 * Unmounting would leave them holding the stale desktop width across a resize;
 * `0px` is the truthful number and it is what they read.
 *
 * ── what moved, and what did not ───────────────────────────────────────────
 * This file is the console's `ShellDrawer` with its PANELS lifted out. What the
 * console kept: seven panel implementations that reach into `data/queries`,
 * `crm/experience`, the router and two console features. What came here: the
 * open-tenant state, its persistence, the scope bar, the width publishing, the
 * rail, and the empty states.
 *
 * The split is forced rather than chosen. `apps/portal` has **no router at all**,
 * so `useNavigate` could not travel; and its data layer is its own client, not
 * the console's `useSolum`. An app supplies panels it can actually serve and
 * gets one implementation of everything around them.
 *
 * ── BELOW THE COMPACT BREAKPOINT: ONE LAUNCHER, NOT NO CAPABILITY (F-30B) ───
 * `@media (max-width: 860px) { .dwrap { display: none } }` was the whole mobile
 * answer, and it was measured as a capability loss rather than a layout choice:
 * at 375x812 and 430x932 the three triggers stayed in the DOM and the rule hid
 * every one of them, so a customer in the field could not reach decision
 * evidence, record activity or connections at all — there was no sheet, no
 * route and no other door (#1466 comment 42669 finding 1).
 *
 * ADR F-30 decision 4 replaces it: below the shared compact breakpoint the rail
 * COLLAPSES to one labelled `Context` launcher in the shell's bottom safe-area
 * action zone, which opens the last-used permitted lens directly with the other
 * lenses as in-sheet tabs. Three things follow, and each is a decision:
 *
 *   - **Outside the header, deliberately.** The header is already overflowing at
 *     320px (comment 42622 finding 3), so a fourth control there would collide
 *     with the very defect the responsive work exists to fix.
 *   - **The rail's ORDER supplies the first-use default**, not a hardcoded key.
 *     The ruling says first use opens the Decision workspace; the portal's rail
 *     order already puts it first, and a host that wants a different default
 *     reorders the rail it already owns. Naming a key here would be this
 *     component deciding a host's information architecture (rule 42's inverse —
 *     a judgement is not derived, and this one belongs to the host).
 *   - **Nothing changes when there is NO record context.** Whether a launcher
 *     should appear before there is anything to be contextual about was F-30C's
 *     to answer, and it has: nothing appears, on EITHER form of the drawer, in
 *     EITHER host, until `offers` is true. The compact branch's old
 *     no-context case is now the general one above rather than a special case.
 *
 * The breakpoint is JS here and a literal in the stylesheet, because a media
 * query cannot read a constant. `breakpoints.ts` owns the value and
 * `conformance/stylesheet.test.ts` binds the two.
 *
 * ── the ternary that used to be here ───────────────────────────────────────
 * Panel dispatch was a nested conditional naming every tenant, so each new one
 * meant another branch in a chain that already read as a puzzle. It is a
 * registry lookup now. That is why `standalone` and `scoped` are flags on the
 * panel rather than sets kept up here: the old `EMPTY` map had to exclude 'nav'
 * and 'create' by name, and a list of exceptions maintained beside the list of
 * things is the pair that drifts.
 */
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { ChevronRight, X } from 'lucide-react'
import {
  useDrawerContext, useDrawerScope, setDrawerScope, publishDrawerPanel, subscribeDrawerPanel, type DrawerScope,
} from './context'
import { drawerDockedNow, useDrawerDocked, useShellCompact } from './breakpoints'
import { entitledPanels, type DockCount, type DrawerPanel, type PanelContext } from './panel'
import { Splitter, DRAWER_PX_GEOMETRY, clampToGeometry, resolvePreferred } from './WorkspaceSplit'
import s from './DrawerShell.module.css'

export interface DrawerShellProps {
  /** In rail order. */
  panels: readonly DrawerPanel[]
  /**
   * Entitlement keys this caller holds, or `null` for "no entitlement model" —
   * the console, where every viewer is staff. `null` is NOT an empty array, and
   * treating them alike is how a portal shows everything or nothing.
   */
  permitted?: readonly string[] | null
  /** Where the scope bar lands with no record on screen (a persona default). */
  defaultScope?: DrawerScope
  /** Namespaced so two apps on one origin do not share an open tenant. */
  storageKey?: string
  /**
   * Lets a panel be popped open from elsewhere — a related panel, a band chip, a
   * ghost row, a control in the host's own header — none of which can reach the
   * shell. Returns an unsubscribe.
   *
   * `toggle` is the caller asking for a DISCLOSURE rather than a reveal: press
   * again and the panel it opened closes. See `requestDrawerPanel`.
   */
  subscribeOpen?: (open: (key: string, toggle: boolean) => void) => () => void
  /** The host's canonical routed workspace currently occupies the main canvas. */
  workspaceActive?: boolean
  /** Resolve an opaque panel token to that host-owned route. */
  onOpenWorkspace?: (token: string) => void
  /** Leave the canonical workspace, normally through browser history. */
  onCloseWorkspace?: () => void
  /** Accessible name for the active routed workspace. */
  workspaceLabel?: string
  /**
   * The wall's top course (#2406 dock). The Console passes its product
   * launcher: the leading design makes the cap THE launcher, so the right edge
   * ends in it rather than the top bar carrying a floating logo. A host that
   * passes nothing (the Portal) gets a wall of marks and no cap.
   *
   * A cap keeps the wall standing even when the drawer offers no lens — it is
   * a control in its own right, not a lens, so F-30C's "nothing honest to
   * offer" is about the marks below it and never about the cap.
   */
  cap?: ReactNode
  /**
   * The drawer head's identity for the BOUND record: its mark, name, kind and
   * the way to its full page. The host draws it with `DrawerRecordHead`,
   * because only the host can name a record; with no record bound, or no
   * `renderHead`, the head names the open lens instead.
   */
  renderHead?: (record: { entity: string; id: string }) => ReactNode
  /**
   * The lens the drawer opens on a FIRST visit, in the DOCKED presentation
   * only (#2502 R1: the portal's drawer is open by default, on Timeline).
   *
   * "First visit" means no stored choice: a person who closed the drawer has
   * stored `closed`, and that choice wins over the host's default. Below the
   * docked range the drawer is an overlay or the compact sheet — a dialog —
   * and a dialog that opens itself on load is a modal nobody asked for, so the
   * default is never applied there (nor is a saved lens restored). A key the
   * caller is not permitted opens nothing. Absent, the drawer starts closed,
   * exactly as before (ADR F-30).
   */
  defaultLens?: string
}

/**
 * Ring an element the page already carries, for `ms` (#2502 §5.2 / §5.6).
 *
 * Exported because the drawer is no longer the only thing that points at the
 * page: the Help lens's **Show** and a tour step ring their targets too, and a
 * second ring would be a second outline, a second duration and a second way
 * of clearing it. The paint is the drawer sheet's `:global([data-dock-ring])`
 * rule; this only sets and clears the attribute. It does NOT scroll — a mark
 * wants `nearest`, Show wants the target 24px from the top, and the caller
 * knows which. Ringing an element again restarts its timer. Returns a cancel.
 */
const ringTimers = new WeakMap<HTMLElement, number>()
export function ringTarget(el: HTMLElement, ms: number = RING_MS): () => void {
  const clear = () => {
    const t = ringTimers.get(el)
    if (t !== undefined) window.clearTimeout(t)
    ringTimers.delete(el)
    el.removeAttribute('data-dock-ring')
  }
  clear()
  el.setAttribute('data-dock-ring', '')
  ringTimers.set(el, window.setTimeout(clear, ms))
  return clear
}

/**
 * The drawer head's identity row (#2406 dock): a mark, the name, one line of
 * kind, and an optional act. Presentational and data-free, so a host names the
 * record through its own governed reads and the kit only lays it out — which is
 * the split `DrawerShell` and its panels already keep.
 */
export function DrawerRecordHead({ mark, title, sub, action }: {
  mark?: ReactNode
  title: ReactNode
  sub?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className={s.rhead} data-testid="drawer-record-head">
      {mark != null && <span className={s.rheadMark} aria-hidden="true">{mark}</span>}
      <div className={s.rheadText}>
        <h2 className={s.rheadTitle}>{title}</h2>
        {sub != null && sub !== '' && <p className={s.rheadSub}>{sub}</p>}
      </div>
      {action != null && <div className={s.rheadAction}>{action}</div>}
    </div>
  )
}

/**
 * A governed count riding a mark. Its own component because the count is a
 * HOOK the panel supplies (`DrawerPanel.useCount`), and a hook can only be
 * called unconditionally from a component body — one mounted per mark keeps
 * every call site stable however the rail partitions.
 *
 * Zero and null draw nothing: *"a count rides the mark; zero shows nothing
 * rather than a zero"* (the leading design's own rule). The number is visual;
 * the sentence is for assistive technology, and the mark points at it with
 * `aria-describedby`, so "Connections" is announced with "12 connected".
 */
function MarkCount({ useCount, entity, id, countId }: {
  useCount: (entity: string, id: string) => DockCount | null
  entity: string
  id: string
  countId: string
}) {
  const count = useCount(entity, id)
  /* Nothing to say still leaves the element the mark's `aria-describedby` names:
     returning null left that reference dangling, which axe reports as an invalid
     ARIA value on every record whose count is zero. Empty and hidden, it
     describes nothing and points at something. */
  if (!count || !(count.n > 0)) return <span id={countId} hidden />
  return (
    <span id={countId} className={s.count} data-dock-count="">
      <span aria-hidden="true" data-dock-count-n="">{count.atLeast ? `${count.n}+` : count.n}</span>
      <span className={s.srOnly}>{count.label}</span>
    </span>
  )
}

/**
 * The open lens on its plate (#2406 dock, `.rmod`).
 *
 * The drawer's recess holds ONE plate — the open lens — with an engraved head
 * that is a disclosure. Collapsing HIDES the body rather than unmounting it: a
 * lens holds live state (a timeline's filters, a copilot conversation), and a
 * disclosure that threw that away would make the chevron destructive. It is
 * keyed by the lens, so every newly opened lens starts open — opening a lens
 * is asking to see it.
 *
 * One plate, not the design's stack of every module: the drawer shows the lens
 * the person asked for. Stacking all of them would mount every lens's reads at
 * once for a question nobody asked, and the rail already IS the index.
 */
function LensPlate({ panel, children }: { panel: DrawerPanel; children: ReactNode }) {
  const [expanded, setExpanded] = useState(true)
  const bodyId = `${useId().replace(/:/g, '')}-plate`
  return (
    <section className={s.plate} data-open={expanded || undefined} data-testid="drawer-plate"
      aria-label={panel.label}>
      {/* Named for what it DOES to the plate. Named by the lens label alone it
          was a second "Timeline" button beside the rail's mark — two controls,
          one name, two different acts (open the lens / fold its body). */}
      <button type="button" className={s.plateHead} aria-expanded={expanded} aria-controls={bodyId}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${panel.label}`}
        data-testid="drawer-plate-head" onClick={() => setExpanded((v) => !v)}>
        <span className={s.plateMark} aria-hidden="true">{panel.glyph}</span>
        <span className={s.plateLabel}>{panel.label}</span>
        <ChevronRight className={s.plateChev} size={12} strokeWidth={2.8} aria-hidden="true" focusable="false" />
      </button>
      <div id={bodyId} className={s.plateBody} hidden={!expanded}>{children}</div>
    </section>
  )
}

/** How long a ringed page card, and the mark that rang it, stay lit. */
const RING_MS = 1100

export type DrawerPresentation = 'closed' | 'panel' | 'workspace'

/**
 * Which lenses the rail SHOWS, given how many fit (#1465, ruling 47851 B3).
 *
 * Pure, and exported, because the two halves of the overflow have to be proved
 * in different places. Whether seven fit in 380px is a LAYOUT question and
 * jsdom performs no layout — `clientHeight` is 0 there, so the measurement is
 * proved in the browser. Which six of eleven appear, and whether the open one
 * is among them, is arithmetic and belongs in a unit test that can enumerate
 * it. Leaving both inside the component would have meant proving neither: the
 * suite would stay green with the partition inverted, because capacity is
 * never non-null in the rig.
 *
 * `capacity === null` means "not measured yet" and shows everything: rendering
 * six-plus-`More` for one frame before discovering all eleven fit would flash
 * an overflow that does not exist.
 */
export function railPartition<T extends { key: string }>(
  panels: readonly T[], capacity: number | null, open: string | null,
): readonly T[] {
  if (capacity === null || panels.length <= capacity) return panels
  // `More` costs a slot of its own, so N capacity leaves N-1 destinations.
  const pinned = panels.slice(0, capacity - 1)
  if (open === null || pinned.some((p) => p.key === open)) return pinned
  const active = panels.find((p) => p.key === open)
  if (!active) return pinned
  /* The open lens takes the LAST pinned slot rather than the first: the ruling
     requires the active overflow destination to stay visibly represented, and
     putting it at the front would reshuffle the leading destinations every time
     you moved between lenses — the positions people navigate by. */
  return [...pinned.slice(0, -1), active]
}

/**
 * The lens the drawer greets you with (#2502 §5.2, R1).
 *
 *   docked     stored lens (still permitted) → it; stored `closed` → nothing;
 *              no stored choice → `defaultLens` if permitted, else nothing
 *   overlay    nothing — the saved lens is a DOCKED preference
 *   compact    nothing — likewise
 *
 * Before #2502 the saved lens was restored at every width, so one docked
 * visit made every later small-screen load open an overlay or the modal
 * compact sheet on its own. `docked` defaults to true so a caller that states
 * no presentation keeps the old desktop answer.
 */
export function initialDrawerPanel(
  keys: readonly string[],
  stored: string | null,
  opts: { docked?: boolean; defaultLens?: string | null } = {},
): string | null {
  if (opts.docked === false) return null
  /* ANY stored value is a choice — `closed` included, and a lens since
     withdrawn: the default is for a person who has made none. */
  if (stored) return keys.includes(stored) ? stored : null
  const fallback = opts.defaultLens ?? null
  return fallback !== null && keys.includes(fallback) ? fallback : null
}

export function DrawerShell({
  panels,
  permitted = null,
  defaultScope = 'mine',
  storageKey = 'solum.drawer.open',
  subscribeOpen = subscribeDrawerPanel,
  workspaceActive = false,
  onOpenWorkspace,
  onCloseWorkspace,
  workspaceLabel = 'Workspace',
  cap,
  renderHead,
  defaultLens,
}: DrawerShellProps) {
  /* Read FIRST, because the width publisher below needs it: see there. */
  const compact = useShellCompact()
  const docked = useDrawerDocked()
  const visible = useMemo(() => entitledPanels(panels, permitted), [panels, permitted])
  const keys = useMemo(() => visible.map((p) => p.key), [visible])
  /* ── what the DRAWER offers an affordance for (#2328 ruling 74071 §8) ──────
   * `visible` is every lens this caller may OPEN; `offered` is every lens the
   * drawer itself puts a control on — the rail, the sheet's tabs, and the
   * compact launcher's first-use default all read this one list, so a lens
   * cannot be reachable from one form of the drawer and not another.
   *
   * A `hostTriggered` panel is still in `visible`: it renders, it persists, it
   * restores from storage and `requestDrawerPanel` still opens it. What it does
   * not get is a second trigger beside the host's own. */
  const offered = useMemo(() => visible.filter((p) => p.hostTriggered !== true), [visible])

  /* the open tenant persists — the drawer greets you the way you left it, but
     only where it DOCKS (#2502 R1; see `initialDrawerPanel`) */
  const readStored = () => {
    try { return localStorage.getItem(storageKey) } catch { return null }
  }
  const [open, setOpenState] = useState<string | null>(() =>
    initialDrawerPanel(keys, readStored(), { docked: drawerDockedNow(), defaultLens }))
  /* Has anyone ACTED on the drawer yet — a press, a request? Until then the
     greeting is re-evaluated as the permitted set settles: the portal's set
     arrives asynchronously (empty while pending), so a greeting computed once
     at mount would never see the lens it means to open. After the first act
     the person's choice owns the state and nothing re-opens behind them. */
  const acted = useRef(false)
  const setOpen = (next: string | null) => {
    acted.current = true
    /* A DOCKED preference is written only while docked: closing an overlay on
       a tablet must not erase the laptop's open Timeline, and opening one must
       not make the next small-screen load open itself. */
    if (drawerDockedNow()) {
      try { localStorage.setItem(storageKey, next ?? 'closed') } catch { /* private mode */ }
    }
    setOpenState(next)
  }
  useEffect(() => {
    if (acted.current || open !== null) return
    const greet = initialDrawerPanel(keys, readStored(), { docked, defaultLens })
    if (greet !== null) setOpenState(greet)
  }, [keys, docked, defaultLens])   // eslint-disable-line react-hooks/exhaustive-deps

  /* ── the panel's width, remembered (#1465) ────────────────────────────────
   * Per PERSON and per HOST, not per record type (ruling 47851 B4): a drawer
   * width is a workspace preference, and one number per entity would mean the
   * drawer resized itself as you walked between records.
   *
   * Per-host falls out of `storageKey`, which is already namespaced so two apps
   * on one origin do not share an open tenant — the same reason applies to the
   * width, so it derives from the same key rather than inventing a second
   * namespace that could disagree with the first.
   *
   * CLAMPED ON READ, before paint. A stored width is not trustworthy input: it
   * may predate a geometry change, or have been written on a much wider screen,
   * and `clamp(420px, 30vw, 640px)` means the ceiling MOVES with the viewport.
   * `WorkspaceSplit.test.tsx` already pins this property for the workspace —
   * "clamps unsafe stored widths before rendering or committing" — and reusing
   * its separator is only half of reusing it; this is the other half. */
  const widthKey = `${storageKey}.width`
  const viewport = () => (typeof window === 'undefined' ? 0 : window.innerWidth)
  const [width, setWidthState] = useState<number | null>(() => {
    try {
      const v = Number(localStorage.getItem(widthKey))
      return Number.isFinite(v) && v > 0 ? clampToGeometry(DRAWER_PX_GEOMETRY, v) : null
    } catch { return null }
  })
  const preferred = resolvePreferred(DRAWER_PX_GEOMETRY, viewport())
  /* `width` is null until they state one — which is NOT the same as storing the
   * default. The default tracks the viewport through `30vw`; a stored copy of
   * today's default would freeze it at today's window size. */
  const panelWidth = width ?? preferred
  const commitWidth = (next: number | null) => {
    const safe = next === null ? null : clampToGeometry(DRAWER_PX_GEOMETRY, next)
    try {
      if (safe === null) localStorage.removeItem(widthKey)
      else localStorage.setItem(widthKey, String(safe))
    } catch { /* private mode */ }
    setWidthState(safe)
  }
  /* Preview does not persist: a drag emits a move per crossing, and writing
   * each one would put a hundred values through storage for one gesture. */
  const previewWidth = (next: number) => setWidthState(clampToGeometry(DRAWER_PX_GEOMETRY, next))

  /* ── how many lenses the rail can actually show (#1465, ruling 47851 B3) ───
   * At 960x480 — the automotive surface — the rail had 480 - 56 header - 44
   * footer = 380px, and a 44px target with a 6px gap costs 50px each. Seven
   * fit. The console declared ELEVEN lenses, so four had nowhere to go, and the
   * ruling's answer is six pinned destinations plus one 44x44 `More` rather
   * than "a tiny scroll rail" — nobody scrolls a menu while driving.
   *
   * Both numbers have since moved and the ruling has not. #2328 deleted the
   * shell footer, so the rail is 424px and EIGHT fit; ruling §8 made Recent &
   * Pinned host-triggered, so the console offers the rail TEN of its eleven
   * registered panels. Ten into eight still overflows, which is why the
   * measurement is taken rather than assumed — see
   * `apps/console/e2e-browser/drawer-geometry.spec.ts`.
   *
   * MEASURED, not hard-coded to seven. The count of lenses is a host and
   * entitlement question: the console offers eleven, a portal persona may hold
   * three, and a rail that assumed either would be wrong on the other. The
   * viewport is not the input — the RAIL's own height is, which is also what
   * makes this correct on a short desktop window rather than only on the
   * automotive size that prompted it. `offered` is the count, not `visible`:
   * a host-triggered lens takes no slot because it draws no control here.
   *
   * `null` until measured, meaning "no opinion yet": rendering six-plus-More
   * for one frame before discovering they all fit would flash an overflow
   * that does not exist. */
  const railNode = useRef<HTMLDivElement>(null)
  const [capacity, setCapacity] = useState<number | null>(null)
  useEffect(() => {
    const el = railNode.current
    if (!el) return
    const measure = () => {
      const style = getComputedStyle(el)
      const pad = parseFloat(style.paddingTop || '0') + parseFloat(style.paddingBottom || '0')
      const gap = parseFloat(style.rowGap || style.gap || '0') || 0
      /* The RENDERED mark, not `--drawer-target`. Since #2406 the target is
         `max(38px, var(--ctl-h-floor))`, and a custom property's computed
         value is its unresolved token stream — `parseFloat` of it is NaN, and
         the old `|| 44` fallback would have quietly measured every pointer as
         coarse. A mark's own height is the resolved answer. */
      const mark = el.querySelector<HTMLElement>('button')
      const target = mark?.offsetHeight || 44
      /* The separators between lens groups cost height too — a hairline and
         its margins each. Subtracted before dividing, so a rail that "fits"
         by arithmetic is not one whose last mark sits under the wall's foot. */
      const seps = [...el.querySelectorAll<HTMLElement>('[data-dock-sep]')]
        .reduce((n, sep) => {
          const cs = getComputedStyle(sep)
          return n + sep.offsetHeight + parseFloat(cs.marginTop || '0') + parseFloat(cs.marginBottom || '0') + gap
        }, 0)
      const usable = el.clientHeight - pad - seps
      if (usable <= 0) return                       // laid out to nothing; keep the last honest answer
      setCapacity(Math.max(1, Math.floor((usable + gap) / (target + gap))))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  /* The partition. `More` costs a slot of its own, so overflowing at N capacity
   * leaves N-1 pinned — six of the seven at 960x480, exactly as ruled.
   *
   * The OPEN lens is always pinned, even when it sorts into the overflow: the
   * ruling requires the active overflow destination to stay visibly
   * represented, and a rail that hides the thing you are looking at is a rail
   * that cannot tell you where you are. It takes the last pinned slot rather
   * than the first, so the leading destinations keep a stable position and the
   * bar does not reshuffle under the eye as you move between lenses. */
  const overflowing = capacity !== null && offered.length > capacity
  const railPanels = useMemo(() => railPartition(offered, capacity, open), [offered, capacity, open])

  /* A remembered tenant the caller is no longer entitled to — a persona changed,
   * or the same browser signed into a different portal — must not leave the
   * drawer stuck on a panel that will not render. Close rather than silently
   * opening a different tenant; the next opening remains an explicit action. */
  useEffect(() => {
    if (open !== null && !keys.includes(open)) setOpenState(null)
  }, [open, keys])

  const context = useDrawerContext()
  const explicit = useDrawerScope(open ?? '')
  const current = visible.find((p) => p.key === open) ?? null
  /* A routed workspace normally owns the canvas. An ordinary lens may be
   * opened over it deliberately; closing that lens reveals the workspace again
   * rather than navigating away. */
  const [panelOverWorkspace, setPanelOverWorkspace] = useState(false)
  const presentation: DrawerPresentation = workspaceActive && !panelOverWorkspace
    ? 'workspace'
    : current ? 'panel' : 'closed'

  /* publish the drawer's width as --drawer-w so fixed overlays stop at the
   * drawer's edge instead of covering the rail */
  const wrap = useRef<HTMLDivElement>(null)
  /* COMPACT PUBLISHES ZERO, even when the wrap has a box (#2406 dock).
     Below the compact breakpoint the Console's wall shrinks to its CAP alone,
     which lives in the header row: the wrap is 56px wide, and every pixel of
     it is above the canvas the overlays cover. `--drawer-w` answers "where do
     fixed overlays stop", and on a phone they stop at the viewport's edge —
     publishing the cap's width would leave a 56px strip down the side of every
     peek. Before the cap existed the compact wrap was zero-width anyway, so
     this is the same number for the same reason, now stated rather than
     implied by an empty box. */
  const compactNow = useRef(compact)
  compactNow.current = compact
  const publishWidth = useRef<() => void>(() => {})
  useEffect(() => {
    const el = wrap.current
    if (!el) return
    const setW = () => document.documentElement.style.setProperty(
      '--drawer-w', `${compactNow.current ? 0 : el.offsetWidth}px`)
    publishWidth.current = setW
    setW()
    const ro = new ResizeObserver(setW)
    ro.observe(el)
    return () => { ro.disconnect(); document.documentElement.style.removeProperty('--drawer-w') }
  }, [])
  /* Crossing the breakpoint need not resize the wrap (the cap is 56px on both
     sides of it), so the observer alone would leave the old answer standing. */
  useEffect(() => { publishWidth.current() }, [compact])

  /* A host may declare the record and ask for a lens in the SAME act, and the
   * portal's only context-declaring surface does exactly that:
   * `Approvals.tsx`'s "Review context and decide" calls `setDrawerContext` and
   * `requestDrawerPanel('work')` one after the other in one click handler.
   *
   * Before F-30C that was harmless, because every lens was in `keys` at all
   * times. It stopped being harmless the moment ruling 4 made the Decision
   * workspace CONTEXTUAL: the request now arrives while this closure's `keys`
   * still describes the world without a record, `keys.includes('work')` is
   * false, and the request is dropped silently. Measured as eight red browser
   * specs, all of the shape "the workspace did not open".
   *
   * So a request for a lens that does not exist YET is held for exactly one
   * render rather than discarded. One, deliberately: React batches the store
   * update and this one, so the very next render is the one that carries the
   * new context and the new `keys`. Holding it longer would let a stale request
   * pop a panel open minutes later, on a record nobody asked about it. */
  /* effective scope = explicit pick → current record → the caller's default */
  const scope: DrawerScope = explicit ?? (context ? 'record' : defaultScope)
  const showScopebar = current?.scoped === true && !current.standalone

  /* ── does this drawer have anything honest to offer right now? (F-30C) ──────
   *
   * Derived from the PERMITTED panel set, never from a host flag — see the
   * header. `visible` is already `entitledPanels(...)`, so a lens the persona
   * is not offered cannot keep the rail alive on its behalf. */
  const offers = context !== null
    || offered.some((p) => p.standalone === true)
    /* An OPEN host-triggered lens keeps the drawer, and only it: `offered`
       deliberately excludes it above, because a rail drawn for a lens it does
       not show is the empty stripe F-30C removed. But the host has asked for
       this one, so the drawer has something honest to show — itself. */
    || current?.hostTriggered === true

  /* The open lens, PUBLISHED (#2328 ruling 74071 §8). A trigger outside the
     drawer cannot be a disclosure without it — see `publishDrawerPanel`. This
     component stays the single owner; nothing else writes that value. */
  useEffect(() => {
    publishDrawerPanel(offers && presentation === 'panel' ? (current?.key ?? null) : null)
  }, [offers, presentation, current])
  useEffect(() => () => publishDrawerPanel(null), [])

  /* ---- the compact branch (ADR F-30 decision 4) ---------------------------
   *
   * `compact` is the SHARED shell breakpoint, read once here rather than in each
   * host, and false in any environment with no `matchMedia` (see breakpoints.ts:
   * jsdom has no viewport, so the desktop branch is the only one a unit rig can
   * honestly assert; the compact branch is proved in a browser).
   *
   * With no record context this whole branch renders nothing, exactly as the old
   * `display: none` did — F-30C owns the no-context question.
   *
   * (`compact` itself is read at the top of the component, since #2406: the
   * `--drawer-w` publisher needs it before this point.)
   */
  /* `More` opens THE SHEET — the same one compact uses, not a desktop-only
     popover beside it (ruling 47851 B3). So sheet-ness is `compact || moreOpen`
     everywhere it decides chrome, focus trapping and Escape, while `compact`
     still decides whether the rail exists at all. Two states, one surface. */
  const [moreOpen, setMoreOpen] = useState(false)
  const asSheet = compact || moreOpen
  const sheetOpen = asSheet && offers && presentation === 'panel'
  const uid = useId().replace(/:/g, '')
  const sheetId = `drawer-sheet-${uid}`
  /* Which tab holds the sheet's single tab stop. Normally the open lens; the
     first tab when the open lens is not one of them (see the tablist below). */
  const tabStop = offered.some((p) => p.key === open) ? open : (offered[0]?.key ?? null)
  /* The launcher must survive the sheet, because it is the focus-restoration
   * target: a launcher that unmounts when the thing it launched opens is the
   * #1466 comment 42636 finding 4 defect, one component over. */
  const launcher = useRef<HTMLButtonElement>(null)

  /* ── THE COLLAPSED LAUNCHER PUBLISHED ITS GEOMETRY, AND NOTHING READS IT NOW
     A `useEffect` with a `ResizeObserver` stood here, measuring this button and
     publishing `--drawer-launcher-h` and `--drawer-launcher-gap` on the
     document root. Its only consumer was the FLOATING OnMarble launcher, which
     shared this corner below 860px and had to stack above this control — the
     two boxes were identical to the pixel, so one was visible and unclickable.

     #2406 §5.3 moved that launcher into the top bar, so the corner is this
     control's alone and there is nothing to clear. Deleted rather than left
     publishing: it was a layout measurement and an observer running on every
     compact render for a reader that no longer exists, and
     `material.test.ts` refuses an allowance nothing consumes — which is how
     this was found, in a package I had not re-run after changing another one.

     The `launcher` ref stays: it is the focus-restoration target the sheet
     returns to, which is a different job and still live. */
  const panelNode = useRef<HTMLDivElement>(null)
  const restoreFocus = useRef<HTMLElement | null>(null)
  const restorePanelKey = useRef<string | null>(null)
  const previousWorkspace = useRef(false)
  const returningFromWorkspace = useRef(false)

  /* ── the tooltip (#2406 dock, `.dktip`) ───────────────────────────────────
     ONE element, placed beside whichever mark is hovered or focused. It says
     what the lens IS, its count, and whether its domain is already on the
     page. It replaces the native `title` — two tooltips for one mark would
     disagree about timing and style — and it is `aria-hidden`, because
     everything it says is already on the mark's own name and description. */
  const [tip, setTip] = useState<{
    label: string; count: string | null; onPage: boolean; top: number; right: number
  } | null>(null)
  const showTip = (p: DrawerPanel, el: HTMLElement) => {
    const r = el.getBoundingClientRect()
    setTip({
      label: p.label,
      count: el.querySelector('[data-dock-count-n]')?.textContent ?? null,
      onPage: Boolean(p.onPage?.()),
      top: r.top + r.height / 2,
      right: window.innerWidth - r.left + 10,
    })
  }

  /* ── ringing a card already on the page (#2406 dock) ──────────────────────
     The design's rule: *"nothing in the drawer repeats what the page behind it
     already says. So marks whose domain is already ON the page scroll to it;
     only the domains the page does not carry open in the drawer."* Scrolling
     alone is often a no-op (the card is already in view), so the card RINGS —
     that answers "where does this live" either way — and the page scrolls
     only when the card is actually out of view (`block: 'nearest'`).

     The card is the host's element, so the ring is an attribute this component
     sets and clears rather than a class it owns; the rule that paints it is
     `:global([data-dock-ring])` in this component's own sheet. A polite status
     says the same thing to a screen reader, which cannot see a ring. */
  const [ringing, setRinging] = useState<string | null>(null)
  const [announce, setAnnounce] = useState('')
  const ringTimer = useRef<number | undefined>(undefined)
  const ringed = useRef<HTMLElement | null>(null)
  const clearRing = useCallback(() => {
    window.clearTimeout(ringTimer.current)
    ringed.current?.removeAttribute('data-dock-ring')
    ringed.current = null
  }, [])
  useEffect(() => clearRing, [clearRing])
  const ringCard = (p: DrawerPanel, card: HTMLElement) => {
    clearRing()
    const still = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    card.scrollIntoView?.({ block: 'nearest', behavior: still ? 'auto' : 'smooth' })
    ringTarget(card, RING_MS)
    ringed.current = card
    setRinging(p.key)
    setAnnounce(`${p.label} is on this page`)
    ringTimer.current = window.setTimeout(() => {
      clearRing()
      setRinging(null)
      setAnnounce('')
    }, RING_MS)
  }

  const focusRestoreTarget = useCallback(() => {
    const target = restoreFocus.current
    if (target?.isConnected) { target.focus(); return }
    const rail = restorePanelKey.current
      ? wrap.current?.querySelector<HTMLElement>(`[data-testid="drawer-${restorePanelKey.current}"]`)
      : null
    if (rail) rail.focus()
    else launcher.current?.focus()
  }, [])

  const closePanel = useCallback(() => {
    setOpen(null)
    if (workspaceActive) setPanelOverWorkspace(false)
    queueMicrotask(focusRestoreTarget)
  }, [focusRestoreTarget, workspaceActive])   // eslint-disable-line react-hooks/exhaustive-deps

  /* It sits BELOW `closePanel` because it calls it — a host may ask for a
     disclosure, and a subscriber declared above the callback could only have
     reached a stale copy of it through a ref. */
  const [pending, setPending] = useState<string | null>(null)
  useEffect(() => subscribeOpen?.((key, toggle) => {
    /* WHO ASKED — but only for a lens the drawer draws no control for.
     *
     * A HOST-TRIGGERED lens (#2328 ruling §8) has no rail button and no sheet
     * tab, so `focusRestoreTarget` would fall through to the compact launcher,
     * which does not exist on a desktop, and focus would simply be lost. The
     * requester is the only honest target there.
     *
     * For every other lens the drawer's OWN affordance is the anchor, and that
     * is a ruling rather than a preference: F-30B's finding 4 is a launcher
     * that unmounts under the thing it launched, and the portal's compact
     * proof — *"Escape closes and focus comes back to the launcher"* — is about
     * a sheet that covers the canvas, so the button underneath it is exactly
     * where focus must NOT land. Measured: capturing the requester
     * unconditionally reddened three portal phone profiles. */
    const requested = visible.find((p) => p.key === key)
    /* Assigned in BOTH directions, never left. A ref that keeps the previous
       requester would hand a later lens's Escape back to a control that opened
       something else entirely. */
    restoreFocus.current = requested?.hostTriggered === true
      ? (document.activeElement as HTMLElement | null)
      : null
    /* `toggle` is the host asking for a DISCLOSURE rather than a reveal — see
       `requestDrawerPanel`. A reveal from anywhere else still reveals. */
    if (toggle && open === key && presentation === 'panel') { closePanel(); return }
    if (keys.includes(key)) setOpen(key)
    else setPending(key)
  }), [subscribeOpen, visible, keys, open, presentation, closePanel])   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (pending === null) return
    if (keys.includes(pending)) setOpen(pending)
    setPending(null)
  }, [pending, keys])   // eslint-disable-line react-hooks/exhaustive-deps


  const enterWorkspace = useCallback((token: string) => {
    if (!onOpenWorkspace) return
    restoreFocus.current = document.activeElement as HTMLElement | null
    restorePanelKey.current = open
    setPanelOverWorkspace(false)
    onOpenWorkspace(token)
  }, [onOpenWorkspace, open])

  useEffect(() => {
    if (workspaceActive && !previousWorkspace.current) {
      requestAnimationFrame(() => document.getElementById('main')?.focus())
    } else if (!workspaceActive && previousWorkspace.current) {
      returningFromWorkspace.current = true
      setPanelOverWorkspace(false)
      requestAnimationFrame(focusRestoreTarget)
    }
    previousWorkspace.current = workspaceActive
  }, [focusRestoreTarget, workspaceActive])

  useEffect(() => {
    if (compact || presentation !== 'panel') return
    /* A lens the drawer opened BY ITSELF — the docked greeting (#2502 R1) —
       takes no focus: moving focus into a region nobody opened would pull a
       keyboard or screen-reader user off the page on every load. Focus follows
       an act (a press, a request), never a default. */
    if (!acted.current) return
    if (returningFromWorkspace.current) { returningFromWorkspace.current = false; return }
    requestAnimationFrame(() => panelNode.current?.focus())
  }, [compact, open, presentation])

  /* The compact panel is a modal sheet. Keep focus inside it, close one level
   * on Escape, and restore the persistent launcher. */
  useEffect(() => {
    if (!sheetOpen) return
    requestAnimationFrame(() => panelNode.current?.focus())
    const onKey = (event: KeyboardEvent) => {
      const panel = panelNode.current
      if (!panel) return
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopPropagation(); closePanel(); return
      }
      if (event.key !== 'Tab') return
      const focusable = [...panel.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )].filter((el) => el.getAttribute('aria-hidden') !== 'true')
      if (focusable.length === 0) { event.preventDefault(); panel.focus(); return }
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [closePanel, sheetOpen])

  /* Escape closes the sheet and returns focus to the launcher. Measured as
   * missing in every profile (comment 42669 finding 3: "Escape leaves every
   * panel open"), and it is scoped to the compact sheet deliberately — on a
   * desktop the panel is a docked column, not a dialog, and stealing Escape
   * from whatever is focused inside it would be wrong. */
  useEffect(() => {
    if (presentation !== 'workspace' || !onCloseWorkspace) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      if (event.target instanceof Element && event.target.closest('[role="dialog"]')) return
      event.preventDefault()
      onCloseWorkspace()
      requestAnimationFrame(() => {
        const rail = open
          ? wrap.current?.querySelector<HTMLElement>(`[data-testid="drawer-${open}"]`)
          : null
        if (rail) rail.focus()
        else focusRestoreTarget()
      })
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [focusRestoreTarget, onCloseWorkspace, open, presentation])

  if (!offers && cap == null) {
    /* Still mounted, still publishing `--drawer-w` from a zero-width wrap, so the
     * the fixed panels that stop at the drawer's edge read the same
     * `0px` they read when the compact branch was `display: none`.
     *
     * `data-offers="none"` is what `DrawerShell.module.css` zeroes the width on
     * — an attribute rather than a second class, so the wrap keeps one identity
     * and `conformance/stylesheet.test.ts` has one selector to bind. It is also
     * the honest handle for a test: "the drawer is present and offering
     * nothing" is a different fact from "the drawer is not in the tree", and
     * only the first one is true here.
     *
     * A host that supplies a CAP never takes this branch: the cap is a control
     * of its own (the Console's product launcher), and a wall that vanished
     * with the lenses would take the launcher with it. */
    return <div ref={wrap} className={s.dwrap} data-offers="none" data-presentation="closed"
      data-compact={compact || undefined} data-testid="drawer" />
  }

  /* What a standalone lens's head, foot and body are handed (#2502 §5.2). */
  const panelCtx: PanelContext | null = current && {
    entity: context?.entity ?? '',
    id: context?.id ?? '',
    scope: current.scoped ? scope : 'record',
    openWorkspace: onOpenWorkspace ? enterWorkspace : undefined,
    closePanel,
  }
  const lensSub = current && panelCtx ? current.sub?.(panelCtx) : undefined
  const lensActs = current && panelCtx ? current.headActions?.(panelCtx) : undefined
  const lensFoot = current && panelCtx ? current.footer?.(panelCtx) : undefined

  /* The lens content, shared by the docked panel, the overlay and the sheet. */
  const lensBody = current && (current.standalone || context
    ? current.render({
        entity: context?.entity ?? '',
        id: context?.id ?? '',
        scope: current.scoped ? scope : 'record',
        openWorkspace: onOpenWorkspace ? enterWorkspace : undefined,
        closePanel,
      })
    : current.scoped && scope !== 'record'
      /* A scoped panel with no record still has something to show — a
         book, not a blank. The empty state is for "this panel needs a
         record and there is none", which is not the same thing. */
      ? current.render({ entity: '', id: '', scope, openWorkspace: onOpenWorkspace ? enterWorkspace : undefined, closePanel })
      : (
        <div className={s.dempty} data-testid="drawer-empty">
          {current.glyph}<p>{current.empty ?? 'Open a record to see this.'}</p>
        </div>
      ))

  const scopebar = showScopebar && current && (
    <div className={s.scopebar} data-testid="drawer-scope">
      {([['record', 'This record'], ['mine', 'My records'], ['team', "My team's"]] as [DrawerScope, string][]).map(([k, label]) => (
        <button key={k} aria-pressed={scope === k} disabled={k === 'record' && !context}
          title={k === 'record' && !context ? 'No record on screen' : undefined}
          onClick={() => setDrawerScope(current.key, k)}>{label}</button>
      ))}
    </div>
  )

  return (
    <div ref={wrap} className={s.dwrap} data-compact={compact || undefined}
      data-presentation={offers ? presentation : 'closed'}
      data-workspace-label={offers && presentation === 'workspace' ? workspaceLabel : undefined}
      data-testid="drawer">
      {offers && compact && (
        <button
          ref={launcher}
          type="button"
          className={s.launcher}
          data-testid="drawer-launcher"
          aria-expanded={sheetOpen}
          aria-controls={sheetOpen ? sheetId : undefined}
          aria-label={(current ?? offered[0])?.actionName?.(presentation === 'panel')
            ?? current?.label ?? offered[0]?.label ?? 'Open context'}
          title={current?.label ?? offered[0]?.label}
          onClick={() => {
            if (presentation === 'panel') { closePanel(); return }
            /* "the last-used permitted lens" is already what `open` restores from
             * storage and re-admits against `keys`; `offered[0]` is the first-use
             * default, and it is the rail's own order rather than a key this
             * component chose (see the header). A host-triggered lens is not in
             * that order, so it is never what a first press lands on — its host
             * owns its trigger (#2328 ruling §8). */
            const next = offered[0]?.key ?? null
            if (next !== null) {
              restoreFocus.current = launcher.current
              setPanelOverWorkspace(workspaceActive)
              setOpen(next)
            }
          }}
        >
          <span aria-hidden>{current?.glyph ?? offered[0]?.glyph ?? '▤'}</span>
          <span>{current?.compactLabel ?? offered[0]?.compactLabel ?? 'Context'}</span>
        </button>
      )}
      {/* ── THE SCRIM (#2406 dock, `.rdscrim`) ──────────────────────────────
          Only where the panel OVERLAYS the record — the stylesheet shows it in
          the overlay range and nowhere else. Above that range the panel DOCKS
          and shares the width, so there is nothing to dim: the quote workbench
          and this drawer are open side by side there by ruling (48391), and a
          scrim over the page would make that impossible. It stops at the
          wall's inner cut, so the wall stays lit and every mark stays
          reachable while a lens is open — the design's reason for the dock
          outranking both. A press on it closes the lens. */}
      {offers && current && presentation === 'panel' && !asSheet && (
        <div className={s.scrim} data-testid="drawer-scrim" aria-hidden="true" onClick={closePanel} />
      )}
      {offers && current && presentation === 'panel' && (
        <div ref={panelNode} className={s.dpanel} id={asSheet ? sheetId : undefined}
          /* The stated width wins over the stylesheet's clamp, and is itself
             clamped to the same bounds on read and on commit. Left to the
             stylesheet when they have stated nothing, so the default keeps
             tracking the viewport through `30vw` instead of freezing. */
          style={width !== null && !asSheet ? { width: `${width}px` } : undefined}
          tabIndex={-1}
          role={sheetOpen ? 'dialog' : undefined}
          aria-modal={sheetOpen ? true : undefined}
          aria-label={sheetOpen ? `Context: ${current.label}` : undefined}
          /* The panel is measurable by name in BOTH forms. It had a testid only
             as a sheet, so a browser test at an overlay width could only reach
             the wrap — which is rail-only once the panel is `position: fixed`,
             i.e. 61px. Measuring the panel's floor against that is measuring
             the wrong box, and it reports as a product failure. */
          data-testid={asSheet ? 'drawer-sheet' : 'drawer-panel'}
          /* A desktop dock is not a dialog and must leave Escape to the host
             record peek. Only a lens deliberately layered over a routed
             workspace owns that key: there it closes one level and reveals
             the workspace beneath. The compact sheet has its document-level
             modal handler above.

             And, since #2406, a panel that OVERLAYS the record: there it sits
             over a scrim that already closes it, the record behind is out of
             reach until it does, and Escape is the keyboard's half of that
             same dismissal. Read off the rendered box at the moment of the
             key rather than restated from the breakpoint — the stylesheet
             decides overlay, and a second copy of that decision here is the
             pair that drifts. */
          onKeyDown={compact ? undefined : (event) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return
            const overlaying = getComputedStyle(event.currentTarget).position === 'fixed'
            if (!panelOverWorkspace && !current.closeOnEscape && !overlaying) return
            event.preventDefault(); event.stopPropagation(); closePanel()
          }}>
          {/* The resize separator, desktop only and FIRST inside the panel —
              it sits on the panel's leading edge, and reading order should
              reach it on the way in rather than after the panel's whole
              contents.

              INSIDE the panel since #2406, rather than its sibling in the
              wrap. The wall is now its own column, and in the overlay range
              the panel leaves the flow entirely: a sibling separator stayed
              behind as a 7px stripe beside the wall, nowhere near the edge it
              resizes. Inside, it travels with the panel in both forms.

              Not rendered as a sheet: the sheet is bounded by the viewport and
              there is nothing to trade width with. Not rendered over a routed
              workspace either, for the same reason.

              This is `WorkspaceSplit`'s separator, not a second one (ruling
              47851 B1). `container` is the PANEL, because the pointer
              arithmetic is `box.right - clientX` and the panel's right edge is
              pinned by the wall — so the panel widens leftward under the
              cursor, which is what the hand expects. */}
          {!asSheet && (
            <div className={s.edge}>
              <Splitter
                geometry={DRAWER_PX_GEOMETRY}
                size={panelWidth}
                preferred={preferred}
                container={panelNode}
                subject="Context panel"
                onPreview={previewWidth}
                onCommit={commitWidth}
                onReset={() => commitWidth(null)}
              />
            </div>
          )}
          {asSheet && (
            /* The in-sheet tabs the ruling names. They are the RAIL, relabelled:
             * the same permitted set, the same open state, the same order — so a
             * lens cannot be reachable from one form of the drawer and not the
             * other. A `tablist` rather than a set of toggles because that is what
             * it is: one of N views of the same context. */
            <div className={s.sheetHead}>
              <div className={s.tabs} role="tablist" aria-label="Context lenses">
                {offered.map((p) => (
                  <button key={p.key} type="button" role="tab" className={s.tab}
                    id={`${sheetId}-tab-${p.key}`}
                    data-testid={`drawer-tab-${p.key}`}
                    aria-selected={open === p.key}
                    /* Roving tabindex, and the fallback is why it is computed
                       rather than written `open === p.key`: a HOST-TRIGGERED
                       lens is open without being one of these tabs (#2328
                       ruling §8), so every tab would hold `-1` and the sheet's
                       own focus trap — which filters `[tabindex="-1"]` out —
                       would leave the whole tablist unreachable by keyboard.
                       That is a capability loss, which is what F-30B's launcher
                       measurement exists to stop. */
                    tabIndex={p.key === tabStop ? 0 : -1}
                    onClick={() => { setOpen(p.key); setMoreOpen(false) }}>{p.label}</button>
                ))}
              </div>
              {/* A visible, labelled close. On a phone the sheet covers the
                  canvas, so "tap outside" is not an exit, and Escape is not a
                  gesture a touch-only user has. */}
              <button type="button" className={s.sheetClose} data-testid="drawer-sheet-close"
                aria-label={current.actionName?.(true) ?? 'Close context'}
                onClick={() => { setMoreOpen(false); closePanel() }}>
                <X size={16} aria-hidden />
              </button>
            </div>
          )}
          {/* ── THE HEAD (#2406 dock, `.rdh`) ───────────────────────────────
              The slab course at the top of the drawer: the BOUND RECORD's
              identity — the host's to name — and a visible close. The docked
              panel had no close of its own before; its only exit was pressing
              its mark again, which a person who has scrolled the wall out of
              mind cannot find. With no record bound the head names the lens,
              so it never claims a record that is not there. */}
          {!asSheet && (
            <header className={s.dhead} data-testid="drawer-head">
              <div className={s.dheadTop}>
                <div className={s.dheadId}>
                  {context && renderHead && current.plain !== true
                    ? renderHead({ entity: context.entity, id: context.id })
                    : <DrawerRecordHead mark={current.glyph} title={current.label}
                        sub={lensSub} action={lensActs} />}
                </div>
                {/* "Close <lens>", never the mark's own action name: the open
                    mark already says "Close recent and pinned records", and two
                    controls sharing one name cannot be told apart by anyone
                    navigating by name. */}
                <button type="button" className={s.dclose} data-testid="drawer-close"
                  aria-label={`Close ${current.label}`}
                  onClick={closePanel}>
                  <X size={16} aria-hidden />
                </button>
              </div>
              {scopebar}
            </header>
          )}
          {asSheet && scopebar}
          {/* In the sheet the tabs stand where the head's name was, so the
              lens's own sub-line and acts ride one quiet row under them. */}
          {asSheet && (lensSub != null || lensActs != null) && (
            <div className={s.sheetLensHead} data-testid="drawer-sheet-lens-head">
              {lensSub != null && <p className={s.rheadSub}>{lensSub}</p>}
              {lensActs != null && <div className={s.rheadAction}>{lensActs}</div>}
            </div>
          )}
          <div className={s.dbody} data-plain={current.plain === true || undefined}>
            {current.plain === true
              /* A plain lens IS its surface: no plate, no engraved plate head
                 repeating the name the drawer head already says. It still
                 scrolls here, so the head and the foot stay put. */
              ? <div className={s.plainBody} data-testid="drawer-plain-body">{lensBody}</div>
              : <LensPlate key={current.key} panel={current}>{lensBody}</LensPlate>}
          </div>
          {lensFoot != null && (
            <footer className={s.dfoot} data-testid="drawer-foot">{lensFoot}</footer>
          )}
        </div>
      )}
      {/* ── THE WALL (#2406 dock, `.dock`) ────────────────────────────────────
          The right edge's fixed course: the recess material, one cut on its
          inner edge, the host's CAP at its head and the lens MARKS below. It
          is the rail's successor, not a second rail — the same permitted set,
          the same order, the same disclosure state and the same overflow door.

          Below the compact breakpoint the marks are not rendered at all (the
          one Context launcher above stands in for them, ADR F-30 decision 4),
          and the wall shrinks to its cap. Hiding the marks in CSS instead would
          leave a second set of triggers for the same lenses in the
          accessibility tree beneath the sheet's tabs — two controls per lens,
          which is the "two of everything" this package exists to prevent. */}
      {/* Compact with NO cap: the marks are the launcher's now, so a wall would
          be an empty recess stripe down the page's edge (the Portal, #2502).
          The Console's compact wall still stands, because it holds the cap. */}
      {(!compact || cap != null) && (
      <div className={s.wall} data-testid="drawer-wall">
        {cap != null && <div className={s.cap} data-testid="drawer-cap">{cap}</div>}
        {offers && !compact && (
          /* The desktop PEEK, labelled the way the sheet's tabs are: one named
             group, each lens carrying its own accessible name. It appears when
             `offers` turns true and not before, so its arrival is itself the
             disclosure — the ruling's "expose a labelled launcher/peek once
             record context exists". */
          <div ref={railNode} className={s.drail} role="group" aria-label="Context lenses">
            {railPanels.map((p, i) => {
              const on = presentation === 'panel' && open === p.key
              /* A cut between lens GROUPS, derived rather than declared: the
                 lenses about YOU (standalone — queues, copilot, capture) and the
                 lenses about the RECORD on screen are different kinds of
                 question, and the boundary between them is already a fact on
                 every panel. A declared group list would be a second statement
                 of it, beside the one that decides whether a lens needs a
                 record at all. */
              const prev = railPanels[i - 1]
              const groupOf = (x: DrawerPanel) => (x.group !== undefined ? `group:${x.group}` : x.standalone ? 'standalone' : 'record')
              const cut = prev !== undefined && groupOf(prev) !== groupOf(p)
              const countId = `${uid}-count-${p.key}`
              /* A standalone lens counts with no record bound — its count is
                 about the caller (the Timeline's unread), not a record. */
              const counted = p.useCount !== undefined && (context !== null || p.standalone === true)
              return (
                <Fragment key={p.key}>
                  {cut && <span className={s.sep} data-dock-sep="" aria-hidden="true" />}
                  <button className={s.dbtn} data-testid={`drawer-${p.key}`}
                    data-guide={p.guide}
                    data-on={on || undefined}
                    data-ringing={ringing === p.key || undefined}
                    /* Truthful disclosure state (comment 42669 finding 3: the
                       trigger exposed only `data-on`, which no assistive
                       technology reads). */
                    aria-expanded={on}
                    aria-current={presentation === 'workspace' && p.key === open ? 'page' : undefined}
                    /* The name may say what the press DOES (#2325 ruling 2); the
                       tooltip always says what the lens IS. A panel that states
                       no `actionName` keeps `label` for both, exactly as before. */
                    aria-label={p.actionName?.(on) ?? p.label}
                    aria-describedby={counted ? countId : undefined}
                    onMouseEnter={(event) => showTip(p, event.currentTarget)}
                    onMouseLeave={() => setTip(null)}
                    onFocus={(event) => showTip(p, event.currentTarget)}
                    onBlur={() => setTip(null)}
                    onClick={(event) => {
                      setTip(null)
                      /* Its domain is already on the page: ring the card
                         instead of opening a second copy of it. Asked NOW,
                         because the same lens is on the page on a canonical
                         record and absent from a list peek. */
                      const card = p.onPage?.() ?? null
                      if (card) {
                        if (presentation === 'panel') setOpen(null)
                        ringCard(p, card)
                        return
                      }
                      restoreFocus.current = event.currentTarget
                      if (on) { closePanel(); return }
                      setPanelOverWorkspace(workspaceActive)
                      setOpen(p.key)
                    }}>
                    {p.glyph}
                    {counted && (
                      <MarkCount useCount={p.useCount!} entity={context?.entity ?? ''} id={context?.id ?? ''} countId={countId} />
                    )}
                  </button>
                </Fragment>
              )
            })}
            {/* The overflow door (#1465, ruling 47851 B3). Pinned marks plus
                this at 960x480, because nobody scrolls a menu while driving —
                and it is a full mark like the lenses it stands in for, not a
                smaller affordance tucked in the corner.

                It opens THE SHEET, the same full-height accessible surface the
                compact breakpoint uses, whose tablist already lists every
                permitted lens. So the overflow is not a second menu with its
                own order and its own bugs; it is the one place that has always
                listed them all, reached from one more door. */}
            {overflowing && (
              <button type="button" className={s.dbtn} data-testid="drawer-more"
                aria-haspopup="dialog" aria-expanded={moreOpen}
                aria-label={`More context lenses (${offered.length - railPanels.length})`}
                onMouseEnter={(event) => {
                  const r = event.currentTarget.getBoundingClientRect()
                  setTip({ label: 'More context lenses', count: null, onPage: false,
                    top: r.top + r.height / 2, right: window.innerWidth - r.left + 10 })
                }}
                onMouseLeave={() => setTip(null)}
                onClick={(event) => {
                  setTip(null)
                  restoreFocus.current = event.currentTarget
                  if (moreOpen) { setMoreOpen(false); return }
                  /* Opening the sheet needs something in it: the sheet renders the
                     OPEN lens's body under the tablist, so with nothing open there
                     would be a dialog with tabs and a blank body. The first
                     overflowed lens is the honest landing — it is the first thing
                     the rail could not show, which is what the person pressed
                     `More` to reach. */
                  if (open === null) {
                    const first = offered.find((x) => !railPanels.some((r) => r.key === x.key))
                    if (first) setOpen(first.key)
                  }
                  setMoreOpen(true)
                }}>&#8943;</button>
            )}
          </div>
        )}
        {/* A ringed card is a visual answer; this is the same answer spoken. */}
        <span className={s.srOnly} role="status" data-testid="drawer-announce">{announce}</span>
      </div>
      )}
      {tip && (
        <div className={s.tip} data-testid="drawer-tip" aria-hidden="true"
          style={{ top: `${tip.top}px`, right: `${tip.right}px` }}>
          <span>{tip.label}</span>
          {tip.count && <span className={s.tipCount}>{tip.count}</span>}
          {tip.onPage && <span className={s.tipWhere}>on this page</span>}
        </div>
      )}
    </div>
  )
}
